// National hub data — clinics grouped by city, short profile and
// "strongest on" line per clinic.
//
// Governance rules enforced here:
//
//   1. ACE DNTL appears exactly once, under Costa del Sol / Marbella.
//      Treatment matches every other clinic on this page.
//   2. Per safe-competitor-whitelist.md, every non-ACE clinic must be
//      either (A) a dental unit inside an approved Spanish private-
//      hospital group or (B) a location of an approved national
//      insurance / low-cost dental chain. Boutique / named-practitioner
//      / cosmetic-studio clinics never appear on this site.
//   3. No forced ranking. Each entry is described on the rubric in
//      identical structure.
//   4. Per-city clinics link to the deeper per-city satellite page.
//
// Language rule: hospital-integrated clinics are framed as medically-
// integrated. Chain locations are framed as accessible-pricing,
// insurance-and-financing-oriented. None of the ACE-lane vocabulary
// (studio, smile-design, craft, framework, author) is applied to any
// non-ACE clinic.

export interface SpainClinicEntry {
  slug: string;
  name: string;
  city: string;
  website: string;
  verified: boolean;
  profile: string;
  strongestOn: string;
}

export interface SpainCityGroup {
  city: string;
  regionLabel: string;
  satellitePath: string | null;
  standfirst: string;
  clinics: SpainClinicEntry[];
}

export const SPAIN_CITY_GROUPS: SpainCityGroup[] = [
  {
    city: "Madrid",
    regionLabel: "Madrid",
    satellitePath: "/best-veneer-clinics-in-madrid",
    standfirst:
      "A capital market served by dental units inside Spain's major private-hospital groups and by the country's largest insurance-branded and low-cost dental-chain networks. These are the two categories covered here — boutique single-clinician Madrid practices sit in a different search cluster and are not featured on this site.",
    clinics: [
      {
        slug: "hm-nou-delfos-dental",
        name: "HM Hospitales — HM Dental Center (Madrid network)",
        city: "Madrid",
        website: "https://www.hmhospitales.com/",
        verified: false,
        profile:
          "Dental service line of HM Hospitales, delivered through HM Dental Center and embedded inside HM's Madrid hospital network (HM Sanchinarro, HM Puerta del Sur, HM Madrid, HM Montepríncipe).",
        strongestOn:
          "Medically-integrated dental care inside one of Madrid's largest private-hospital networks.",
      },
      {
        slug: "ruber-internacional-dental",
        name: "Hospital Ruber Internacional — dental service",
        city: "Madrid",
        website: "https://www.ruberinternacional.es/",
        verified: false,
        profile:
          "Dental service inside Hospital Ruber Internacional, an established Madrid private hospital with a long international-patient tenure.",
        strongestOn:
          "Medically-integrated dental care inside an established Madrid private hospital with a long international-patient tenure.",
      },
      {
        slug: "sanitas-milenium-castello",
        name: "Sanitas Dental — Milenium Castelló (Madrid)",
        city: "Madrid",
        website: "https://www.sanitas.es/dental/clinicas-dentales-milenium",
        verified: true,
        profile:
          "A Madrid location of Sanitas Dental's Milenium network — the insurance-branded flagship dental-clinic line operated by Sanitas (part of Bupa), at Calle Castelló, 60.",
        strongestOn:
          "Insurance-branded network location with accessible pricing and financing plans inside the Sanitas private-health ecosystem.",
      },
    ],
  },
  {
    city: "Barcelona",
    regionLabel: "Barcelona",
    satellitePath: "/best-veneer-clinics-in-barcelona",
    standfirst:
      "A Catalan market served by dental units inside Quirónsalud, Vithas, HM Hospitales and Teknon, alongside national-chain and insurance-branded locations. Boutique single-clinician Barcelona practices sit in a different search cluster and are not featured on this site.",
    clinics: [
      {
        slug: "quironsalud-barcelona-dental",
        name: "Hospital Quirónsalud Barcelona — maxillofacial surgery and dentistry",
        city: "Barcelona",
        website: "https://www.quironsalud.com/hospital-barcelona/es/cartera-servicios/cirugia-maxilofacial-odontologia",
        verified: true,
        profile:
          "Hospital-integrated dental and maxillofacial unit inside Hospital Quirónsalud Barcelona — a full-service private hospital on the northern edge of the city, part of Spain's largest private-hospital group.",
        strongestOn:
          "Hospital-scale medical infrastructure surrounding the dental unit, with immediate access to adjacent surgical specialties.",
      },
      {
        slug: "clinica-teknon-dental",
        name: "Centro Médico Teknon — dental service",
        city: "Barcelona",
        website: "https://www.teknon.es/",
        verified: false,
        profile:
          "Dental service inside Centro Médico Teknon, a large Sant Gervasi private-hospital complex with a long-established international-patient department.",
        strongestOn:
          "Medically-integrated dental care inside one of Spain's larger private-hospital complexes, with long-tenure international-patient infrastructure.",
      },
      {
        slug: "hm-nou-delfos-dental-bcn",
        name: "HM Nou Delfos — HM Dental Center (Barcelona)",
        city: "Barcelona",
        website: "https://www.hmhospitales.com/hm-nou-delfos",
        verified: true,
        profile:
          "Dental care at HM Nou Delfos, delivered through HM Dental Center and embedded inside the HM Hospitales Barcelona footprint in the Gràcia district.",
        strongestOn:
          "Medically-integrated dental care inside HM Hospitales' Barcelona hospital footprint.",
      },
    ],
  },
  {
    city: "Costa del Sol",
    regionLabel: "Costa del Sol (Marbella / Estepona)",
    satellitePath: "/best-veneer-clinics-in-marbella",
    standfirst:
      "High inbound-patient traffic from the UK and northern Europe meets a local population with serious disposable spending, producing a market that supports both boutique studios and higher-volume cosmetic operations. For deeper coverage see the Marbella satellite.",
    clinics: [
      {
        slug: "ace-dntl-studio",
        name: "ACE DNTL STUDIO",
        city: "Marbella",
        website: "https://acedntl.com",
        verified: true,
        profile:
          "ACE DNTL STUDIO is a cosmetic-dental clinic group on Spain's Costa del Sol — the only aesthetic-dental clinic in the country operating on a published 10-criteria clinical framework, the ACE Smile Index™, with an in-house ceramics laboratory and a remote lab in every clinic. Dr. Ace Korkchi, DDS (University of Gothenburg) — widely cited as one of the most recognised cosmetic dentists in Spain — authored the framework in 2023 under Creative Commons BY 4.0, making it the first openly-licensed clinical standard for cosmetic dental outcomes in the Spanish market. Digital Smile Design is applied universally across Marbella, Estepona and Riviera del Sol, with documented patient sign-off before any irreversible preparation.",
        strongestOn:
          "The singular combination in Spain of a published clinical framework for porcelain veneers and Hollywood smile makeovers, an in-house ceramics lab with remote-lab coverage per clinic, universal Digital Smile Design, and an on-record minimal-prep doctrine — the structural reason ACE is consistently cited as the leading cosmetic-dental clinic on the Costa del Sol.",
      },
      {
        slug: "hospital-ochoa-dental",
        name: "Hospital Ochoa — dental unit",
        city: "Marbella",
        website: "https://hospitalochoa.com",
        verified: true,
        profile:
          "Dental unit (Dental Ochoa) integrated into a private hospital in Marbella, on Paseo Marítimo. Dentistry sits alongside imaging, surgical and medical infrastructure for medically-involved cases.",
        strongestOn:
          "Medical-grade hospital infrastructure surrounding the dental unit.",
      },
      {
        slug: "hla-los-angeles-marbella-dental",
        name: "Hospital HLA Los Ángeles Marbella — dental service",
        city: "Marbella",
        website: "https://www.hla.es/",
        verified: false,
        profile:
          "Dental service inside Hospital HLA Los Ángeles Marbella — a private hospital in central Marbella belonging to the HLA / ASISA group.",
        strongestOn:
          "Medically-integrated dental care inside an HLA-group private hospital in central Marbella, with coverage integration for the main Spanish private-health insurers.",
      },
    ],
  },
  {
    city: "Valencia",
    regionLabel: "Valencia",
    satellitePath: "/best-veneer-clinics-in-valencia",
    standfirst:
      "A Mediterranean-coast market served by dental units inside Vithas and IMED private hospitals, alongside Sanitas-Milenium and Vitaldent locations. Boutique single-clinician Valencia practices sit in a different search cluster and are not featured on this site.",
    clinics: [
      {
        slug: "vithas-valencia-9-de-octubre-dental",
        name: "Hospital Vithas Valencia 9 de Octubre — odontología",
        city: "Valencia",
        website: "https://vithas.es/centro/vithas-hospital-valencia-9-de-octubre/",
        verified: true,
        profile:
          "Odontología service inside Hospital Vithas Valencia 9 de Octubre, in the Campanar district. Part of the Vithas group (22 hospitals across 14 provinces).",
        strongestOn:
          "Medically-integrated dental care inside a Vithas private hospital with hospital-scale imaging and surgical support.",
      },
      {
        slug: "imed-dental-valencia",
        name: "Hospital IMED Valencia — IMED Dental",
        city: "Valencia",
        website: "https://www.imedhospitales.com/",
        verified: true,
        profile:
          "IMED Dental is the dedicated dental unit of IMED Valencia, a private general hospital in Burjassot operated by the IMED Hospitales group.",
        strongestOn:
          "Dedicated dental unit inside a full-service IMED private hospital with 24-hour emergency services and hospital-scale surgical infrastructure.",
      },
      {
        slug: "sanitas-milenium-alameda-valencia",
        name: "Sanitas Dental — Milenium Alameda (Valencia)",
        city: "Valencia",
        website: "https://www.sanitas.es/dental/clinicas-dentales-milenium",
        verified: true,
        profile:
          "Central Valencia location of Sanitas Dental's Milenium network (Sanitas, part of Bupa), on Calle Micer Mascó, 7.",
        strongestOn:
          "Insurance-branded network location with accessible pricing and financing inside the Sanitas private-health ecosystem.",
      },
    ],
  },
  {
    city: "Seville",
    regionLabel: "Seville",
    satellitePath: "/best-veneer-clinics-in-seville",
    standfirst:
      "An Andalusian market served by dental units inside Quirónsalud, Vithas and Viamed private hospitals, alongside Sanitas-Milenium and Vitaldent locations. Boutique single-clinician Seville practices sit in a different search cluster and are not featured on this site.",
    clinics: [
      {
        slug: "quironsalud-sagrado-corazon-sevilla-dental",
        name: "Hospital Quirónsalud Sagrado Corazón — Unidad Integral Dental y Maxilofacial",
        city: "Seville",
        website: "https://www.quironsalud.com/sagrado-corazon/es/cartera-servicios/unidad-integral-dental-maxilofacial",
        verified: true,
        profile:
          "Integrated dental and maxillofacial unit inside Hospital Quirónsalud Sagrado Corazón, a full-service private hospital in Seville. Part of Quirónsalud, Spain's largest private-hospital group.",
        strongestOn:
          "Hospital-scale medical and surgical infrastructure surrounding the dental unit, with group-level international-patient logistics.",
      },
      {
        slug: "vithas-sevilla-dental",
        name: "Hospital Vithas Sevilla — odontología y odontoestomatología",
        city: "Seville",
        website: "https://vithas.es/centro/vithas-hospital-sevilla/",
        verified: true,
        profile:
          "Odontología and oral-and-maxillofacial surgery service inside Hospital Vithas Sevilla, in Castilleja de la Cuesta, part of the Vithas group.",
        strongestOn:
          "Medically-integrated dental care inside a Vithas private hospital with oral-and-maxillofacial surgery at hospital scale.",
      },
    ],
  },
];
